import React from 'react';
import moment from 'moment';
import { ChevronDown, ChevronUp, Pencil, Trash2, Check, XCircle, Send, RefreshCw } from 'lucide-react';
import { formatStatus, getStatusColor } from '../utils/statusFormatter';

const VideoCard = ({
  video,
  user,
  expandedVideo,
  setExpandedVideo,
  handleEdit,
  handleDelete,
  handleApprove,
  handleReject,
  handleUnapproveVideo,
  setSelectedVideo,
  setShowSubmitModal,
  loading,
  userRole
}) => {
  const publishPhase = video?.phases?.find(phase => phase.name === "PUBLISH");
  const publishDate = publishPhase?.endDate ? moment(publishPhase.endDate).format("MMM D, YYYY") : "No Publish Phase";

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      {/* Header with date and status */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <span className="text-sm text-gray-500">{publishDate}</span>
        </div>
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(video?.status)}`}>
          {formatStatus(video?.status)}
        </span>
      </div>

      {/* Topic with expand/collapse */}
      <button
        onClick={() => setExpandedVideo(expandedVideo === video._id ? null : video._id)}
        className="flex items-start text-left w-full mb-3"
        aria-expanded={expandedVideo === video._id}
        aria-label={`${expandedVideo === video._id ? 'Collapse' : 'Expand'} video details`}
      >
        {expandedVideo === video._id ? (
          <ChevronUp className="h-4 w-4 mr-2 mt-0.5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 mr-2 mt-0.5 text-gray-500 flex-shrink-0" />
        )}
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{video.topic}</h3>
        </div>
      </button>

      {/* Video details */}
      <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3">
        <span className="bg-gray-100 px-2 py-1 rounded">
          {video?.storyType?.durationRange}
        </span>
        <span className="bg-gray-100 px-2 py-1 rounded">
          {video?.videoStyle?.name?.split('_').join(' ')}
        </span>
        <span className="bg-gray-100 px-2 py-1 rounded">
          {video?.storyType?.name
            ?.toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          }
        </span>
      </div>

      {/* Expanded content */}
      {expandedVideo === video._id && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          {video.phases && video.phases.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Phases</h4>
              <div className="space-y-1">
                {video.phases.map((phase, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="text-gray-600">{phase.name}:</span>
                    <span className="text-gray-900">
                      {moment(phase.startDate).format("MMM D")} - {moment(phase.endDate).format("MMM D, YYYY")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {video.url && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Video URL</h4>
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline break-all"
              >
                {video.url}
              </a>
            </div>
          )}

          {video.scriptUrl && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Script URL</h4>
              <a
                href={video.scriptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline break-all"
              >
                {video.scriptUrl}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
        {video.status === "PENDING_APPROVAL" && user?.role &&
         (user?.role === "ADMIN" || user?.role === "APPROVER") && (
          <>
            <button
              onClick={() => handleApprove(video)}
              disabled={loading}
              className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Approve video"
            >
              <Check className="h-3 w-3 mr-1" />
              Approve
            </button>
            <button
              onClick={() => handleReject(video)}
              disabled={loading}
              className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Reject video"
            >
              <XCircle className="h-3 w-3 mr-1" />
              Reject
            </button>
          </>
        )}

        {video.status === "SUBMISSION_OPEN" && (
          <button
            onClick={() => {
              setSelectedVideo(video);
              setShowSubmitModal(true);
            }}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
            aria-label="Submit video URLs"
          >
            <Send className="h-3 w-3 mr-1" />
            Submit URLs
          </button>
        )}

        {video.status === "SUBMISSION_NOT_ACCEPTED" && (
          <button
            onClick={() => {
              setSelectedVideo(video);
              setShowSubmitModal(true);
            }}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700"
            aria-label="Re-submit video URLs"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Re-submit URLs
          </button>
        )}

        {(video.status === "APPROVED_FOR_PO" || video.status === "NOT_APPROVED_FOR_PO") &&
         user?.role && (user?.role === "ADMIN" || user?.role === "APPROVER") && (
          <button
            onClick={() => handleUnapproveVideo(video)}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            aria-label="Unapprove video"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Unapprove
          </button>
        )}

        <button
          onClick={() => handleEdit(video)}
          className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded"
          aria-label="Edit video"
        >
          <Pencil className="h-3 w-3 mr-1" />
          Edit
        </button>

        {userRole === "ADMIN" && (
          <button
            onClick={() => handleDelete(video)}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded"
            aria-label="Delete video"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoCard;